import { assert } from '@flipster/utils/assert';

import InjectedMessageHandlerTransport from './InjectedMessageHandlerTransport';
import MessagePortTransport from './MessagePortTransport';
import discoverMessageTransportType from './discoverMessageTransportType';
import { throwWhenTimeout } from './misc';
import type { MessageTransport, PostMessage } from './type';
import { webkitCloseMessageTransport, webkitPostMessage } from './webkit';

const offlineQueue = new Map<string, MessageEvent<string>>();

function appendOfflineQueue(e: MessageEvent) {
  if (typeof e.data === 'string') {
    offlineQueue.set(e.data, e);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', appendOfflineQueue);
}

function messageOnce(sessionId: string, handler: (e: MessageEvent<string>) => void) {
  const messageId = `prex/createMessagePort/${sessionId}`;
  function listener(e: MessageEvent<string>) {
    if (e.data === messageId) {
      window.removeEventListener('message', listener);
      handler(e);
    }
  }
  if (offlineQueue.has(messageId)) {
    const event = offlineQueue.get(messageId);
    assert(event != null, new Error('Unreachable'));
    offlineQueue.delete(messageId);
    return handler(event);
  }
  window.addEventListener('message', listener);
}

export async function getMessageTransport<HostMetadata, GuestMetadata, IncomingMessage, OutgoingMessage>(
  channelName: string,
  metadata: GuestMetadata,
  onMetadata: (metadata: HostMetadata) => void,
  onMessage: (incomingMessage: IncomingMessage) => void,
  abortSignal: AbortSignal,
): Promise<MessageTransport<IncomingMessage, OutgoingMessage>> {
  if (abortSignal.aborted) throw abortSignal.reason;
  const transportInfo = await discoverMessageTransportType<HostMetadata, GuestMetadata>(
    channelName,
    metadata,
    abortSignal,
  );
  const { sessionId, type } = transportInfo;
  const closeMessageTransport = () => {
    if (type === 'WEBKIT') webkitCloseMessageTransport(channelName, sessionId);
    else window.PrexJavascriptInterface?.closeMessageTransport(channelName, sessionId);
  };
  if (abortSignal.aborted) {
    closeMessageTransport();
    throw abortSignal.reason;
  }
  abortSignal.addEventListener('abort', closeMessageTransport);
  onMetadata(transportInfo.metadata);
  const result = await Promise.race([
    throwWhenTimeout(3000, () => new Error('Handshake failed.')),
    new Promise<MessageTransport<IncomingMessage, OutgoingMessage>>((resolve, reject) => {
      messageOnce(sessionId, (e) => {
        if (abortSignal.aborted) reject(e);
        if (type === 'MESSAGE_PORT') {
          const port = e.ports?.[0];
          if (!port) reject(new Error('No message port given.'));
          else resolve(new MessagePortTransport(sessionId, port, onMessage, closeMessageTransport));
        } else {
          const postMessage: PostMessage<OutgoingMessage> =
            type === 'WEBKIT'
              ? (outgoingMessage) => webkitPostMessage(sessionId, outgoingMessage)
              : (outgoingMessage) =>
                  window.PrexJavascriptInterface?.messageHandler?.(sessionId, JSON.stringify(outgoingMessage));
          resolve(new InjectedMessageHandlerTransport(sessionId, onMessage, postMessage, closeMessageTransport));
        }
      });
    }),
  ]);
  return result;
}
